import React, { useState } from 'react';
import ReactDOM from 'react-dom';
// CommonModal 사용하지 않음
import TodoEditModal from './TodoEditModal';
import { updateTodoStatus, deleteTodo } from '../../api/todoApi';
import '../../css/todo.css';

/**
 * To-Do 조회 모달 (공통)
 *
 * Props:
 *   todo       조회할 To-Do 객체
 *   onClose    모달 닫기 콜백
 *   onUpdated  수정/상태변경 후 목록 갱신 콜백
 *   onDeleted  삭제 후 목록 갱신 콜백
 */

const REF_TYPE_LABEL  = { board: '게시판', approval: '전자결재', resource: '자원예약', schedule: '일정', pms_checklist: '프로젝트', pms_comment: '프로젝트', pms_feed: '프로젝트' };

function TodoViewModal({ todo, onClose, onUpdated, onDeleted, onShowOriginal }) {
  const [showEdit, setShowEdit]   = useState(false);
  const [loading,  setLoading]    = useState(false);
  const token = sessionStorage.getItem('token');
  const navigate = window.reactRouterNavigate || null; // 외부에서 주입 가능성 고려

  const isComplete = todo.status === 'complete';

  // 상태 변경 (완료 ↔ 미완료)
  const handleStatusToggle = async () => {
    setLoading(true);
    try {
      const newStatus = isComplete ? 'incomplete' : 'complete';
      await updateTodoStatus(todo.todo_id, newStatus, token);
      if (onUpdated) onUpdated();
      onClose();
    } catch {
      alert('상태 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 삭제
  const handleDelete = async () => {
    if (!window.confirm('To-Do를 삭제하시겠습니까?')) return;
    setLoading(true);
    try {
      await deleteTodo(todo.todo_id, token);
      if (onDeleted) onDeleted();
      onClose();
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 수정 완료 후(수정모달 닫을 때 상세까지 같이 닫힘)
  const handleEditDone = () => {
    setShowEdit(false);
    if (onUpdated) onUpdated();
    onClose();
  };

  if (showEdit) {
    return (
      <TodoEditModal
        todo={todo}
        onClose={() => {
          setShowEdit(false);
          onClose();
        }}
        onUpdated={handleEditDone}
      />
    );
  }

  // Portal로 body에 렌더링, 반드시 하나의 부모 <div>로 감싸기
  return ReactDOM.createPortal(
    <div>
      <div
        className="todo-modal-overlay"
        style={{
          position: 'fixed', zIndex: 2200, left: 0, top: 0, width: '100vw', height: '100vh',
          background: 'rgba(33,37,41,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >
        <div
          className="todo-register-layer"
          style={{
            minWidth: 560, maxWidth: 900, minHeight: 0, maxHeight: '92vh',
            background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(15,23,42,0.13)',
            padding: 0, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* 타이틀바 */}
          <div
            className="todo-layer-title-bar"
            style={{background: '#1976f7', color: '#fff', fontWeight: 600, fontSize: 17, padding: '0 24px', height: 44, display: 'flex', alignItems: 'center', borderRadius: '12px 12px 0 0', position: 'relative'}}>
            <span style={{flex: 1, textAlign: 'left'}}>To-Do 조회</span>
            <button
              onClick={onClose}
              style={{background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', marginLeft: 8, lineHeight: 1, padding: 0, opacity: 0.85, marginRight: 10}}
              title="닫기"
              tabIndex={0}
            >×</button>
          </div>

          {/* 내용 스크롤 영역 */}
          <div style={{padding: '12px 12px 0 12px', overflowY: 'auto'}}>

            {/* 등록일/완료일 한 줄에 (제목 위) */}
            {(todo.reg_date || (isComplete && todo.completed_at)) && (
              <div className="todo-layer-row" style={{gap: 16, alignItems: 'center', minHeight: 32}}>
                <div style={{display: 'flex', alignItems: 'center', flex: 1, gap: 8, minHeight: 32}}>
                  <label className="todo-layer-label" style={{minWidth: 48, height: 32, display: 'flex', alignItems: 'center'}}>등록일</label>
                  <div style={{minWidth: 0, flex: 1, background: '#f8f9fa', color: '#222', fontSize: 11, fontWeight: 500, lineHeight: '20px', padding: '0 8px', borderRadius: 4, height: 20, display: 'flex', alignItems: 'center'}}>
                    {todo.reg_date ? todo.reg_date.replace('T', ' ').slice(0, 16) : '-'}
                  </div>
                </div>
                {isComplete && todo.completed_at && (
                  <div style={{display: 'flex', alignItems: 'center', flex: 1, gap: 8, minHeight: 32}}>
                    <label className="todo-layer-label" style={{minWidth: 48, height: 32, display: 'flex', alignItems: 'center'}}>완료일</label>
                    <div style={{minWidth: 0, flex: 1, background: '#f8f9fa', color: '#222', fontSize: 11, fontWeight: 500, lineHeight: '20px', padding: '0 8px', borderRadius: 4, height: 20, display: 'flex', alignItems: 'center'}}>
                      {todo.completed_at ? todo.completed_at.replace('T', ' ').slice(0, 16) : '-'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* [추가] 프로젝트명 (프로젝트 연동 데이터인 경우에만 표시) */}
            {todo.ref_type?.startsWith('pms_') && todo.add_title && (
              <div className="todo-layer-row" style={{alignItems: 'center', minHeight: 32}}>
                <label className="todo-layer-label" style={{display: 'flex', alignItems: 'center', height: 32}}>프로젝트</label>
                <div style={{flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, height: 32}}>
                  <div
                    style={{
                      background: '#f8f9fa',
                      flex: '1 1 0',
                      minWidth: 0,
                      maxWidth: 420,
                      color: '#0d6efd',
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: '20px',
                      padding: '0 8px',
                      borderRadius: 4,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title={todo.add_title}
                  >
                    {todo.add_title}
                  </div>
                </div>
              </div>
            )}

            {/* 제목 (등록일/완료일 아래, badge 우측) */}
            <div className="todo-layer-row" style={{alignItems: 'center', minHeight: 32}}>
              <label className="todo-layer-label" style={{display: 'flex', alignItems: 'center', height: 32}}>{todo.ref_type === 'pms_checklist' || todo.ref_type === 'pms_comment' ? '상세항목' : '제목'}</label>
              <div style={{flex: 1, display: 'flex', alignItems: 'center', minWidth: 0, height: 32}}>
                <div
                  style={{
                    background: '#f8f9fa',
                    flex: '1 1 0',
                    minWidth: 0,
                    maxWidth: 420,
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: '#222',
                    fontSize: 11,
                    fontWeight: 500,
                    lineHeight: '20px',
                    padding: '0 8px',
                    borderRadius: 4,
                    height: 20,
                    display: 'block',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  title={todo.title}
                >
                  {todo.title}
                </div>
                <span className={`badge ${isComplete ? 'bg-success' : 'bg-warning text-dark'}`} style={{marginLeft: 8, flexShrink: 0}}>
                  {isComplete ? '완료' : '미완료'}
                </span>
              </div>
            </div>

            {/* 등록구분/중요도 한 줄에 50:50 배치 (제목 아래) */}
            <div className="todo-layer-row" style={{display: 'flex', alignItems: 'center', minHeight: 32, gap: 16}}>
              {/* 등록구분(왼쪽, 50%) */}
              <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, minHeight: 32}}>
                <label className="todo-layer-label" style={{display: 'flex', alignItems: 'center', height: 32}}>등록구분</label>
                <div style={{background: '#f8f9fa', color: '#222', fontSize: 11, fontWeight: 500, lineHeight: '20px', padding: '0 8px', borderRadius: 4, height: 20, display: 'flex', alignItems: 'center', minWidth: 0, flex: 1}}>{REF_TYPE_LABEL[todo.ref_type] || todo.ref_type}</div>
              </div>
              {/* 중요도(오른쪽, 50%) */}
              <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, minHeight: 32}}>
                <label className="todo-layer-label" style={{fontSize: '11px', display: 'flex', alignItems: 'center', height: 32}}>중요도</label>
                <div className="todo-layer-chip-group" style={{display: 'flex', gap: 6, alignItems: 'center', height: 32, minHeight: 32}}>
                  {[
                    { value: 'high', label: '높음' },
                    { value: 'normal', label: '보통' },
                    { value: 'low', label: '낮음' },
                  ].map(opt => {
                    let chipClass = `todo-layer-chip todo-layer-chip--priority-${opt.value}`;
                    if (todo.priority === opt.value) chipClass += ' active';
                    return (
                      <span
                        key={opt.value}
                        className={chipClass}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 32, minWidth: 36, fontSize: 11, fontWeight: 500, verticalAlign: 'middle',
                          background: todo.priority === opt.value ? '#198754' : '#e9ecef',
                          color: todo.priority === opt.value ? '#fff' : '#222',
                          border: 'none',
                          cursor: 'default',
                          userSelect: 'none',
                          boxShadow: 'none',
                          pointerEvents: 'none',
                          padding: '0 8px',
                        }}
                      >
                        {opt.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 기한 칩들과 날짜(라벨+값)를 한 줄에 모두 표시 */}
            <div className="todo-layer-row todo-layer-row--chips" style={{alignItems: 'center', gap: 8, minHeight: 32}}>
              <label className="todo-layer-label" style={{fontSize: '11px', height: 32, display: 'flex', alignItems: 'center'}}>기한</label>
              <div className="todo-layer-chip-group" style={{display: 'flex', gap: 6, alignItems: 'center', height: 32}}>
                {[
                  { value: 'none', label: '기한 없음' },
                  { value: 'today', label: '오늘' },
                  { value: 'tomorrow', label: '내일' },
                  { value: 'custom', label: '날짜 선택' },
                ].map(opt => {
                  let chipClass = 'todo-layer-chip';
                  if (todo.due_type === opt.value) chipClass += ' active';
                  return (
                    <span
                      key={opt.value}
                      className={chipClass}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 32, minWidth: 36, fontSize: 11, fontWeight: 500, verticalAlign: 'middle',
                        background: todo.due_type === opt.value ? '#198754' : '#e9ecef',
                        color: todo.due_type === opt.value ? '#fff' : '#222',
                        border: 'none',
                        cursor: 'default',
                        userSelect: 'none',
                        boxShadow: 'none',
                        pointerEvents: 'none',
                        padding: '0 8px',
                      }}
                    >
                      {opt.label}
                    </span>
                  );
                })}
              </div>
              {/* 날짜: 기한 없음이 아니면 라벨+값+기한지남 한 줄에 */}
              {todo.due_type !== 'none' && (
                (() => {
                  // 오늘 날짜 (yyyy-mm-dd)
                  const todayStr = new Date().toISOString().slice(0, 10);
                  const isOverdue =
                    todo.status === 'incomplete' &&
                    todo.due_date &&
                    todo.due_date < todayStr;
                  return (
                    <div style={{display: 'flex', alignItems: 'center', gap: 4, marginLeft: 12, minHeight: 32, flex: 1}}>
                      <label className="todo-layer-label" style={{fontSize: '11px', minWidth: 28, height: 32, display: 'flex', alignItems: 'center'}}>날짜</label>
                      <div style={{minWidth: 0, width: 110, background: '#f8f9fa', color: '#222', fontSize: 11, fontWeight: 500, lineHeight: '20px', padding: '0 8px', borderRadius: 4, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'flex-start'}}>
                        {todo.due_date || '-'}
                      </div>
                      <div style={{flex: 1, display: 'flex', justifyContent: 'flex-end'}}>
                        {isOverdue && (
                          <span style={{color: '#d32f2f', fontWeight: 700, fontSize: 12, background: '#ffeaea', borderRadius: 4, padding: '2px 10px', height: 24, display: 'flex', alignItems: 'center', marginLeft: 8}}>
                            [기한지남]
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>



            {/* 메모 */}
            <div className="todo-layer-row">
              <label className="todo-layer-label">메모</label>
              <div className="form-control form-control-sm" style={{background: '#f8f9fa', height: 100, overflowY: 'auto', whiteSpace: 'pre-line'}}>
                {todo.memo ? todo.memo : null}
              </div>
            </div>
          </div>

          {/* 버튼 */}
          <div className="todo-layer-actions" style={{
            padding: '0px 30px 12px 30px', // 왼쪽 30px, 오른쪽 30px
            minHeight: 65,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8
          }}>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => {
                if (todo.ref_type?.startsWith('pms_') && todo.add_id1) {
                  let tab = 'checklist';
                  if (todo.ref_type === 'pms_comment') tab = 'comments';
                  if (todo.ref_type === 'pms_feed') tab = 'comments';
                  const path = todo.add_id2 
                    ? `/pms/workspace/${todo.add_id1}?taskId=${todo.add_id2}&tab=${tab}&focusId=${todo.ref_id}`
                    : `/pms/workspace/${todo.add_id1}?tab=${tab}&focusId=${todo.ref_id}`;
                  
                  if (typeof window.reactRouterNavigate === 'function') {
                    window.reactRouterNavigate(path);
                  } else if (navigate) {
                    navigate(path);
                  } else {
                    window.location.href = path;
                  }
                  if (onClose) onClose();
                } else if (todo.ref_type === 'schedule' && todo.ref_id) {
                  if (typeof window.reactRouterNavigate === 'function') {
                    window.reactRouterNavigate(`/schedule/calendar?viewId=${todo.ref_id}`);
                  } else if (navigate) {
                    navigate(`/schedule/calendar?viewId=${todo.ref_id}`);
                  } else {
                    window.location.href = `/schedule/calendar?viewId=${todo.ref_id}`;
                  }
                  if (onClose) onClose();
                } else if (todo.ref_type === 'resource' && todo.ref_id) {
                  if (typeof window.reactRouterNavigate === 'function') {
                    window.reactRouterNavigate(`/reservations/calendar?viewId=${todo.ref_id}`);
                  } else if (navigate) {
                    navigate(`/reservations/calendar?viewId=${todo.ref_id}`);
                  } else {
                    window.location.href = `/reservations/calendar?viewId=${todo.ref_id}`;
                  }
                } else if (typeof onShowOriginal === 'function') {
                  if (todo.ref_type === 'board') {
                    onShowOriginal('board', todo.ref_id);
                  } else if (todo.ref_type === 'approval') {
                    onShowOriginal('approval', todo.ref_id);
                  } else {
                    window.open(`/todo/${todo.todo_id}`, '_blank');
                  }
                } else {
                  window.open(`/todo/${todo.todo_id}`, '_blank');
                }
              }}
            >
              원본보기
            </button>
            <div style={{display: 'flex', gap: 8}}>
              <button
                className={`btn btn-sm ${isComplete ? 'btn-outline-warning' : 'btn-success'}`}
                onClick={handleStatusToggle}
                disabled={loading}
              >
                {isComplete ? '미완료로 변경' : '완료 처리'}
              </button>
              <button className="btn btn-sm btn-outline-primary" onClick={() => setShowEdit(true)}>
                수정
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={handleDelete} disabled={loading}>
                삭제
              </button>
              <button className="btn btn-sm btn-secondary" onClick={onClose}>
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default TodoViewModal;
